import React from "react";
import QuickBooksDataSource from "./QuickBooksDataSource/QuickBooksDataSource";
import ServiceTitanDataSource from "./ServiceTitanDataSource/ServiceTitanDataSource";
import LeadGenerationMethod from "./LeadGenerationMethod/LeadGenerationMethod";
import SwitchIntegrationModal from "./SwitchIntegrationModal/SwitchIntegrationModal";
import {
  INTEGRATION_LABELS,
  useIntegrations,
} from "../../hooks/useIntegrations";

interface SectionProps {
  children: React.ReactNode;
  description: string;
  step: string;
  title: string;
}

const Section: React.FC<SectionProps> = ({
  children,
  description,
  step,
  title,
}) => (
  <section>
    <div className="flex items-center gap-3">
      <span className="rounded-md border border-gray-200 bg-white px-2 py-0.5 font-mono text-xs text-gray-500 dark:border-slate-700 dark:bg-slate-800 dark:text-gray-400">
        {step}
      </span>
      <h2 className="text-lg font-semibold tracking-tight text-gray-900 dark:text-white">
        {title}
      </h2>
    </div>
    <p className="mb-5 mt-1.5 text-sm text-gray-500 dark:text-gray-400">
      {description}
    </p>
    {children}
  </section>
);

const Integrations: React.FC = () => {
  const {
    activeIntegration,
    canSwitchIntegration,
    closeSwitchModal,
    companyProfile,
    expandedSources,
    handleConfirmSwitch,
    handleSelectIntegration,
    intacctId,
    integrationsStatus,
    isQuickBooksConfigured,
    isQuickBooksConnected,
    isServiceTitanConfigured,
    isServiceTitanMappingComplete,
    isSwitching,
    switchIntegrationTarget,
    toggleSource,
  } = useIntegrations();

  return (
    <div className="space-y-10">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
          Integrations
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
          Connect your data sources to power KPIs, then choose where your leads
          come from. You can connect both sources at once — they work
          independently.
        </p>
      </div>

      <Section
        description="Connect one or both. Each source feeds KPIs and can be used as a lead generation source."
        step="01"
        title="Data sources"
      >
        <div className="space-y-4">
          {companyProfile && (
            <>
              <QuickBooksDataSource
                companyUuid={companyProfile.uuid}
                intacctId={intacctId}
                isExpanded={expandedSources.includes("quickbooks")}
                onToggle={() => toggleSource("quickbooks")}
                status={integrationsStatus?.quickBooksOnlineIntegration}
              />
              <ServiceTitanDataSource
                companyUuid={companyProfile.uuid}
                isConfigured={isServiceTitanConfigured}
                isExpanded={expandedSources.includes("servicetitan")}
                onToggle={() => toggleSource("servicetitan")}
              />
            </>
          )}
        </div>
      </Section>

      <Section
        description="Choose where leads come from. Only one method is active at a time — connected sources unlock automatically."
        step="02"
        title="Lead generation method"
      >
        <LeadGenerationMethod
          activeIntegration={activeIntegration}
          canSwitchIntegration={canSwitchIntegration}
          isQuickBooksConfigured={isQuickBooksConfigured}
          isQuickBooksConnected={isQuickBooksConnected}
          isServiceTitanConfigured={isServiceTitanConfigured}
          isServiceTitanMappingComplete={isServiceTitanMappingComplete}
          onSelect={handleSelectIntegration}
        />
      </Section>

      <SwitchIntegrationModal
        activeLabel={
          activeIntegration ? INTEGRATION_LABELS[activeIntegration] : ""
        }
        canSwitchIntegration={canSwitchIntegration}
        isLoading={isSwitching}
        onClose={closeSwitchModal}
        onConfirm={handleConfirmSwitch}
        open={!!switchIntegrationTarget}
        targetLabel={
          switchIntegrationTarget
            ? INTEGRATION_LABELS[switchIntegrationTarget]
            : ""
        }
      />
    </div>
  );
};

export default Integrations;
